import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { v4 as uuidv4 } from 'uuid'

import { useStores } from '../../../cls/stores/MobXProvider'

import {
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	CustomTable,
	BetweenDates
} from '../../../components/Shared'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actLoadBudgetList,
	actUndoOrDeleteBudget,
	calculateBudgetOverview
} from '../../../stores/ui-lst-budget/actions'
import {
	actSaveBudget,
	newBudget,
	requireBudgetById
} from '../../../stores/budget/actions'
import {
	Button,
	Popconfirm,
	Popover,
	Select,
	Space,
	Table,
	Tag,
	Tooltip
} from 'antd'
import { InfoOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import dayjs from 'dayjs'
import { SERVER_DATE_FORMAT } from '../../../config'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import { runInAction } from 'mobx'

/**
 * @component LstItem
 * @description Renders a single row for the Budget list table.
 * Includes currency formatting, conditional tag styling, info popover, and permission-based dropdown actions.
 *
 * @param {string} id - The unique budget item ID.
 * @param {boolean} isFirst - Used to set DOM IDs for guided tour targets.
 * @param {string} selectedCurrency - The currency in which values should be displayed.
 * @returns {JSX.Element}
 */
const LstItem = observer(({ id, isFirst, selectedCurrency }) => {
	const navigate = useNavigate()
	const { t } = useSmartTranslation()
	const { users, categories } = useStores()
	const { loggedInUser, loggedInUserId } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const budget = requireBudgetById(id)

	const selfPermission =
		budget.created_by === loggedInUser.user_id || isAdmin || isSuperAdmin

	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	/**
	 * @function confirmUndoOrDelete
	 * @description Toggles deletion or undoes deletion of a budget item based on current `to_delete` flag.
	 */
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)

		if (!!budget.to_delete) {
			actUndoOrDeleteBudget(id, false).catch(console.error)
		} else {
			actUndoOrDeleteBudget(id, true).catch(console.error)
		}
	}

	// Define the default actions available for this item
	const _actions = [
		{
			key: 'view',
			label: t('single.view'),
			value: 'view',
			disabled: !selfPermission && !users.canUser('read', 'budget')
		}
	]

	// Give edit and delete access to only admins or the document's creator
	if (isAdmin || isSuperAdmin || budget.created_by === loggedInUserId) {
		_actions.push({
			key: 'data',
			label: t('__edit%'),
			value: 'data',
			disabled: !selfPermission && !users.canUser('read', 'budget')
		})
		_actions.push({
			key: 'delete',
			label: t('single.delete'),
			value: 'delete',
			disabled: !selfPermission && !users.canUser('delete', 'budget')
		})
	}

	// Handle the action selected from the dropdown
	/**
	 * @function handleActions
	 * @description Handles dropdown action selection for each budget row (view, edit, delete, undo).
	 * @param {string} value - The selected action key from the dropdown.
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<Link id={isFirst ? 'target-itm-link' : ''} to={`${id}/view`}>
					{budget.name}
				</Link>
			</td>
			<td>
				<Tooltip
					title={
						budget.budget_type === 'in'
							? 'Total income'
							: 'Total expenses'
					}
				>
					<div id={isFirst ? 'target-itm-type' : ''}>
						<Tag
							color={
								budget.budget_type === 'in' ? 'green' : 'red'
							}
						>
							{budget.budget_type === 'in' ? 'IN' : 'OUT'}
						</Tag>
					</div>
				</Tooltip>
			</td>
			<td id={isFirst ? 'target-itm-value' : ''}>
				{budget.convertCurrency(selectedCurrency)} {selectedCurrency}
			</td>
			<td id={isFirst ? 'target-itm-period' : ''}>
				<BetweenDates
					dateFormat={SERVER_DATE_FORMAT}
					dates={[budget.period_start, budget.period_end]}
				/>
			</td>
			<td id={isFirst ? 'target-itm-info' : ''}>
				<CustomTable
					style={{ maxWidth: '200px' }}
					data={[
						[
							{
								item: (
									<Tooltip
										title={t(
											'__assigned-to%_user.possessive.single'
										)}
									>
										<Tag
											color="default"
											id={
												isFirst ? 'target-itm-user' : ''
											}
										>
											{budget.assigned_user_id ? (
												<UserString
													userId={
														budget.assigned_user_id
													}
													path="name"
												/>
											) : (
												'-'
											)}
										</Tag>
									</Tooltip>
								)
							}
						],
						[
							{
								item: (
									<Tooltip title={t('single.category')}>
										<span
											id={
												isFirst
													? 'target-itm-category'
													: ''
											}
										>
											{categories[budget?.category_id]
												?.name || '-'}
										</span>
									</Tooltip>
								)
							}
						]
					]}
				/>
			</td>
			<td>
				<Space
					style={{ width: '100%' }}
					id={isFirst ? 'target-itm-actions' : ''}
					direction="vertical"
				>
					<Space>
						<Popover
							content={() => (
								<CustomTable
									data={[
										[
											{
												item: (
													<strong>
														{t('single.info')}
													</strong>
												)
											}
										],
										[
											t('single.updated-last'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															budget.modifiedTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-at'),
											{
												item: (
													<FormatedTimestamp
														timestamp={
															budget.createdTs
														}
														formatedAs="DD.MM.YYYY hh:mm"
													/>
												)
											}
										],
										[
											t('single.created-by.label'),
											{
												item: (
													<UserString
														userId={
															budget.created_by
														}
														path="name"
													/>
												)
											}
										]
									]}
								/>
							)}
							trigger="hover"
						>
							<Button
								id={isFirst ? 'target-itm-info' : ''}
								color="default"
								icon={<InfoOutlined />}
							/>
						</Popover>
						<Select
							value={t('single.actions')}
							options={_actions}
							onChange={handleActions}
							style={{ width: 120 }}
						/>
					</Space>
					{budget.to_delete && (
						<>
							<span>
								{t('single.delete-on')}
								<FormatedTimestamp
									timestamp={budget.to_delete}
								/>
							</span>
						</>
					)}
				</Space>
				<Popconfirm
					title={
						budget.to_delete
							? t('single.confirm-undo-delete')
							: t('single.confirm-delete')
					}
					open={showConfirmUndoOrDelete}
					onConfirm={confirmUndoOrDelete}
					onCancel={() => setConfirmUndoOrDelete(false)}
					okType="link"
					placement="topLeft"
				/>
			</td>
		</tr>
	)
})
LstItem.propTypes = {
	id: PropTypes.string.isRequired
}

/**
 * @component BudgetLst
 * @description
 * Displays a list of budget entries (income/expenses) with filtering, overview summary, permission-based actions, and guided tour support.
 *
 * It includes:
 * - Overview table (IN, OUT, Net Total)
 * - Filters (currency, assigned user, period, etc.)
 * - Item actions (view, edit, delete) with permission checks
 * - Guided tour support with highlighted targets
 * - Currency conversion and conditional visibility
 *
 * This component interacts with MobX stores (`uiLstBudget`, `categories`, `users`) to manage UI state and data.
 *
 * @returns {React.ReactElement}
 *
 * @access Admin-only
 * @example
 * <Route path="budget" element={<BudgetLst />} />
 */
const BudgetLst = observer(() => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { uiLstBudget, appType, users, tourStore } = useStores()
	const { loggedInUserId } = users

	// state for budget overview
	const [budgetOverview, setBudgetOverview] = useState({})

	const bySearch = uiLstBudget.search
	const byDel = uiLstBudget.del
	const selectedCurrency = uiLstBudget.filter.currency
	const byAssignedUser = uiLstBudget.filter.assigned_user_id

	// List of filters that should be hidden
	const hideFilters = [
		'priority',
		'category',
		'by-user',
		'for-user',
		'orderBy',
		'progress',
		'deleted',
		'toggleBtn'
	]

	// Load budget whenever filters change
	useEffect(() => {
		actLoadBudgetList().catch((err) => {
			console.error('Error loading budgets:', err.message)
		})
	}, [bySearch, byDel, byAssignedUser, appType.appIs])

	// Load budget overview filters change
	useEffect(() => {
		setBudgetOverview(calculateBudgetOverview(selectedCurrency))
	}, [uiLstBudget.ids, selectedCurrency])

	const budgetLst = getTourArrayByKeys('budgetLst', ['all'])

	/**
	 * @function startTour
	 * @description Initializes the guided tour for the budget list screen with UI targets and demo data injection if needed.
	 */
	const startTour = () => {
		runInAction(() => {
			// Open advance filter when we start tour
			uiLstBudget.filterExpanded = true
		})

		const hasItems = uiLstBudget.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() =>
				actUndoOrDeleteBudget(newId, true, true).catch(console.error)
			)

			const demoObj = {
				name: t('__demo%_budget'),
				period_start: dayjs().format('YYYY-MM-DD'),
				period_end: dayjs().add(7, 'day').format('YYYY-MM-DD'),
				assigned_user_id: loggedInUserId,
				created_by: loggedInUserId,
				value: 500,
				currency: 'usd'
			}
			actSaveBudget(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('budgetLst-tour', [
			{
				title: t('__overview%_budget'),
				description: t('single.section.budget'),
				target: () => document.getElementById('target-budget-overview')
			},
			{
				title: t('single.search.label'),
				description: t('single.section.budget'),
				target: () => document.getElementById('target-filter-search')
			},
			{
				title: t('__select.item%_currency'),
				description: t('single.section.budget'),
				target: () => document.getElementById('target-filter-currency')
			},
			{
				title: t('__assigned%_user'),
				description: t('single.section.budget'),
				target: () => document.getElementById('target-filter-for-user')
			},
			{
				title: t('__select.item%single.start-date'),
				target: () =>
					document.getElementById('target-filter-period-start')
			},
			{
				title: t('__select.item%single.end-date'),

				target: () =>
					document.getElementById('target-filter-period-end')
			},
			{
				title: t('__add%single.new'),
				target: () => document.getElementById('target-filter-addnew')
			},

			...budgetLst
		])
	}
	//
	// Define the columns for the budget overview table
	const columns = [
		{
			title: t('single.in'),
			dataIndex: 'in',
			key: 'in',
			render: (text) => (
				<Tooltip title={t('__total%single.income')}>
					<Tag color="green">{text}</Tag>
				</Tooltip>
			)
		},
		{
			title: t('single.out'),
			dataIndex: 'out',
			key: 'out',
			render: (text) => (
				<Tooltip title={t('__total%single.expenses')}>
					<Tag color="red">{text}</Tag>
				</Tooltip>
			)
		},
		{
			title: 'Total',
			dataIndex: 'total',
			key: 'total',
			render: (text) => (
				<Tooltip title={t('_balance.net')}>
					<Tag color={'default'}>{text}</Tag>
				</Tooltip>
			)
		}
	]

	// Prepare the data source for the budget overview table
	const dataSource = [
		{
			key: '1',
			in: budgetOverview.budget_in,
			out: budgetOverview.budget_out,
			total: budgetOverview.budget_in - budgetOverview.budget_out
		}
	]

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_budget') }}
				buttonProps={{
					onClick: startTour
				}}
			/>
			<div
				id="target-budget-overview"
				style={{
					width: '300px',
					border: '1px solid #e8e8e8',
					padding: '5px',
					borderRadius: '10px',
					marginBottom: '10px'
				}}
			>
				<Table
					dataSource={dataSource}
					columns={columns}
					pagination={false}
					showHeader
					size="small"
					style={{ width: '100%', borderCollapse: 'collapse' }}
					rowClassName={(record, index) =>
						index === dataSource.length - 1 ? 'no-border' : ''
					}
				/>
			</div>
			<Filter
				uiStore={uiLstBudget}
				isForBudget={true}
				hide={hideFilters}
				addNew={() => {
					const newId = uuidv4()
					newBudget(newId).then(() => {
						navigate(`/budget/${newId}/edit`)
					})
				}}
			/>
			<LstTable
				LstItem={LstItem}
				store={uiLstBudget}
				selectedCurrency={selectedCurrency}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-budget">{t('single.name')}</th>
					<th id="target-type">{t('single.type')}</th>
					<th id="target-value">{t('single.value')}</th>
					<th id="target-period">{t('__period')}</th>
					<th id="target-info">{t('single.info')}</th>
					<th id="target-actions">{t('_action.multiple')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})
export default BudgetLst
